import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { cssMap } from '@compiled/react';

import Button from '@atlaskit/button/default/button';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { MorphingShapeLoader } from '@atlassian/rovo-platform-ui-components/primitives/MorphingShapeLoader';
import { RainbowProgressBar } from '@atlassian/rovo-platform-ui-components/primitives/RainbowProgressBar';
import { EllipsesAnimation } from '@atlassian/rovo-platform-ui-components/primitives/EllipsesAnimation';
import { ShimmerText } from '@atlassian/rovo-platform-ui-components/primitives/ShimmerText';

import { isExperimentEnabled } from '@confluence/feature-experiments';
import { type FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';

import { useCancelledAnalytics } from '../analytics.ts';
import { useSuggestionsLoadingProgress } from '../useSuggestionsLoadingProgress.ts';
import {
	useRotatingLoadingMessage,
	useSuggestionsLoadingMessages,
} from '../useRotatingLoadingMessage.ts';

import { SuggestionsGridBackground } from './SuggestionsGridBackground.tsx';
import { SuggestionsReviewIllustration } from './SuggestionsReviewIllustration.tsx';

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.200'),
		height: '100%',
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
	},
	loadingContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.200'),
		width: '80%',
	},
	loadingText: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: token('space.050'),
	},
	ellipsis: {
		marginInlineStart: token('space.025'),
	},
});

const i18n = defineMessages({
	cancelButton: {
		id: 'suggestions-panel.loading-state.cancel-button',
		defaultMessage: 'Stop suggesting',
		description:
			'Button label to cancel/stop Rovo from generating suggestions in the suggestions panel',
	},
	labsCancelButton: {
		id: 'suggestions-panel.loading-state.cancel-review-button',
		defaultMessage: 'Cancel',
		description:
			'Button label to cancel/stop Rovo from generating suggestions in the suggestions panel',
	},
	working: {
		id: 'suggestions-panel.loading-state.working',
		defaultMessage: 'Working:',
		description: 'Label above the current progress message while reviewing content',
	},
});

type SuggestionsLoadingStateProps = {
	/** Animates the indicator when the initial Review state has already been shown. */
	hasShownInitialState?: boolean;
	/**
	 * Name of the agent doing the review, used to name it in the first loading message.
	 * Falls back to the generic Rovo copy when absent.
	 */
	agentName?: string;
	api?: FullPageEditorPresetAPI;
	/**
	 * `true` while the reviewing agent is still loading. The loading message then holds on
	 * "Preparing agent" (no rotation) until `agentName` resolves.
	 */
	isAgentLoading?: boolean;
	/**
	 * Epoch ms when the thinking session started. Anchors progress to the floating bar.
	 */
	startedAt?: number | null;
	/** Message rotation starts when the agent is ready; defaults to the session start. */
	rotationStartedAt?: number | null;
	/** Shared facade target so this bar fills to the same value as the floating bar. */
	progressTarget?: number | null;
};

export const SuggestionsLoadingState = ({
	hasShownInitialState = false,
	agentName,
	api,
	isAgentLoading,
	startedAt,
	rotationStartedAt,
	progressTarget,
}: SuggestionsLoadingStateProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const loadingMessages = useSuggestionsLoadingMessages({ agentName, isAgentLoading });
	const loadingMessage = useRotatingLoadingMessage(loadingMessages, rotationStartedAt ?? startedAt);
	const progress = useSuggestionsLoadingProgress({ startedAt, progressTarget });
	const fireCancelledAnalytics = useCancelledAnalytics(api?.analytics?.actions.fireAnalyticsEvent);

	const handleCancel = useCallback(() => {
		fireCancelledAnalytics();
		api?.aiSuggestions?.actions?.setThinkingState('idle');
	}, [api, fireCancelledAnalytics]);

	if (!isExperimentEnabled('platform_editor_ai_content_suggested_edits_labs')) {
		return (
			<div css={styles.container} data-testid="suggestions-panel-loading-state">
				<div css={styles.loadingContainer}>
					<MorphingShapeLoader testId="suggestions-panel-thinking-indicator" size="large" />
					<ShimmerText size="medium">
						{loadingMessage}
						<EllipsesAnimation isAnimated />
					</ShimmerText>
					<RainbowProgressBar
						testId="suggestions-panel-progress-bar"
						label={loadingMessage}
						progress={progress}
						appearance="static-rainbow"
					/>
				</div>
				<Button onClick={handleCancel}>{formatMessage(i18n.cancelButton)}</Button>
			</div>
		);
	}

	return (
		<SuggestionsGridBackground variant="loading" testId="suggestions-panel-loading-state">
			<div css={styles.loadingContainer}>
				<SuggestionsReviewIllustration hasShownInitialState={hasShownInitialState} isLoading />
				<div css={styles.loadingText}>
					<Text as="p" size="medium" align="center" color="color.text.subtlest">
						{formatMessage(i18n.working)}
					</Text>
					<ShimmerText size="medium">
						{loadingMessage}
						<span css={styles.ellipsis}>
							<EllipsesAnimation isAnimated />
						</span>
					</ShimmerText>
				</div>
				<RainbowProgressBar
					testId="suggestions-panel-progress-bar"
					label={loadingMessage}
					progress={progress}
					appearance="static-rainbow"
				/>
			</div>
			<Button onClick={handleCancel}>{formatMessage(i18n.labsCancelButton)}</Button>
		</SuggestionsGridBackground>
	);
};
